import { DateTimeUtc, DateTimeUtcService, lt, useAppClockService } from '@mews/framework';
import { Tag } from '@optimus-web/core';
import { PolicyException } from '../../../../../types';

type PolicyOverrideStatus = 'Active' | 'Scheduled' | 'Expired' | null;

const determinePolicyOverrideStatus = (
    nowUtc: DateTimeUtc,
    timeUnitIntervals: Array<{
        firstTimeUnitStartUtc: string;
        lastTimeUnitStartUtc: string;
    }>
): PolicyOverrideStatus => {
    if (
        timeUnitIntervals.some(({ firstTimeUnitStartUtc, lastTimeUnitStartUtc }) => {
            const firstTimeUnitStartUtcDate = DateTimeUtcService.parse(firstTimeUnitStartUtc);
            const lastTimeUnitStartUtcDate = DateTimeUtcService.parse(lastTimeUnitStartUtc);

            return (
                DateTimeUtcService.isGreaterOrEqual(nowUtc, firstTimeUnitStartUtcDate) &&
                DateTimeUtcService.diffDays(nowUtc, lastTimeUnitStartUtcDate) < 1
            );
        })
    ) {
        return 'Active';
    }

    if (
        timeUnitIntervals.some(({ firstTimeUnitStartUtc }) => {
            const firstTimeUnitStartUtcDate = DateTimeUtcService.parse(firstTimeUnitStartUtc);

            return DateTimeUtcService.isLess(nowUtc, firstTimeUnitStartUtcDate);
        })
    ) {
        return 'Scheduled';
    }

    if (
        timeUnitIntervals.some(({ lastTimeUnitStartUtc }) => {
            const lastTimeUnitStartUtcDate = DateTimeUtcService.parse(lastTimeUnitStartUtc);
            return DateTimeUtcService.diffDays(nowUtc, lastTimeUnitStartUtcDate) > 0;
        })
    ) {
        return 'Expired';
    }

    return null;
};

interface PolicyExceptionStatusCellProps {
    timeUnitIntervals: PolicyException['timeUnitIntervals'];
}

export const PolicyExceptionStatusCell = ({ timeUnitIntervals }: PolicyExceptionStatusCellProps) => {
    const appClockService = useAppClockService();
    const now = appClockService.getAppNowUtc();
    const status = determinePolicyOverrideStatus(now, timeUnitIntervals);

    switch (status) {
        case 'Active':
            return (
                <Tag variant="success" outline>
                    {lt('RateGroupPolicyExceptionStatusActive')}
                </Tag>
            );
        case 'Scheduled':
            return (
                <Tag variant="warning" outline>
                    {lt('RateGroupPolicyExceptionStatusScheduled')}
                </Tag>
            );
        case 'Expired':
            return <Tag outline>{lt('RateGroupPolicyExceptionStatusExpired')}</Tag>;
        default:
            return null;
    }
};
